import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Grid,
  Alert,
  Autocomplete,
  CircularProgress,
  Tooltip,
  Snackbar,
  Link,
} from '@mui/material';

import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon,
  Image as ImageIcon,
  InsertDriveFile as FileIcon,
} from '@mui/icons-material';

import MUIDataTable from "mui-datatables";
import { createTheme, ThemeProvider, useTheme } from '@mui/material/styles';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import barcodeApi from '../services/barcodeApi';
import { getImageUrl } from '../config/api.config';

// Просим barcode-модуль перечитать кэш товаров после изменений.
// Fire-and-forget: ошибка не должна ломать основной сценарий
const refreshBarcodeProducts = () => {
  barcodeApi.refreshProducts().catch((error) => {
    console.warn('Не удалось обновить кэш товаров barcode-модуля:', error.message);
  });
};

// Пустая форма товара: имена полей совпадают с полями коллекции products
const EMPTY_FORM = {
  product_name: '',
  product_code: '',
  category: '',
  crops: [],
  product_rating: 0,
  product_best_before: '',
  product_description: '',
  product_manufacturer: '',
  product_safeperiod: '',
  product_consumption: '',
  product_composition: '',
  product_implement: '',
  where_to_apply: '',
  how_to_apply: '',
  product_compatible: '',
  product_mech: '',
  product_form: '',
  product_pack: '',
  ozon: '',
  video: '',
  verify: '',
  images: [],
  files: [],
};

const AdminProductsPage = () => {
  const { canEdit, isAdmin } = useAuth();
  // Состояния для данных
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  // Товары, ожидающие подтверждения удаления, и функция сброса выбора в таблице
  const [productsToDelete, setProductsToDelete] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const clearSelectionRef = useRef(null);

  // Состояния для модального окна
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [fileUploading, setFileUploading] = useState(false);
  const fileInputRef = useRef(null);
  const docInputRef = useRef(null);

  // Категории и культуры для autocomplete
  const [categories, setCategories] = useState([]);
  const [cropOptions, setCropOptions] = useState([]);

  // Состояния для уведомлений
  const [alert, setAlert] = useState({ show: false, message: '', severity: 'success' });

  // Загрузка данных
  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await apiService.adminProducts.getAll(1, 0);
      const data = response.data;

      if (data.success) {
        setProducts(data.data);
      } else {
        setAlert({ show: true, message: data.message || 'Ошибка загрузки данных', severity: 'error' });
      }
    } catch (error) {
      setAlert({ show: true, message: 'Ошибка подключения к серверу', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await apiService.adminProducts.getCategories();
      if (response.data.success) setCategories(response.data.data);
    } catch (error) {
      console.error('Ошибка загрузки категорий:', error);
    }
  };

  const fetchCrops = async () => {
    try {
      const response = await apiService.adminProducts.getCrops();
      if (response.data.success) setCropOptions(response.data.data);
    } catch (error) {
      console.error('Ошибка загрузки культур:', error);
    }
  };

  useEffect(() => {
    fetchProducts();
    fetchCategories();
    fetchCrops();
  }, []);

  // Обработчики для модального окна
  const handleOpenDialog = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setFormData({
        ...EMPTY_FORM,
        product_name: product.product_name || '',
        product_code: String(product.product_code || ''),
        category: product.category || '',
        crops: Array.isArray(product.crops) ? product.crops : [],
        product_rating: Number(product.product_rating) || 0,
        product_best_before: product.product_best_before || '',
        product_description: product.product_description || '',
        product_manufacturer: product.product_manufacturer || '',
        product_safeperiod: product.product_safeperiod || '',
        product_consumption: product.product_consumption || '',
        product_composition: product.product_composition || '',
        product_implement: product.product_implement || '',
        where_to_apply: product.where_to_apply || '',
        how_to_apply: product.how_to_apply || '',
        product_compatible: product.product_compatible || '',
        product_mech: product.product_mech || '',
        product_form: product.product_form || '',
        product_pack: product.product_pack || '',
        ozon: product.ozon || '',
        video: product.video || '',
        verify: product.verify || '',
        images: Array.isArray(product.images) ? product.images : [],
        files: Array.isArray(product.files) ? product.files : [],
      });
    } else {
      setEditingProduct(null);
      setFormData(EMPTY_FORM);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingProduct(null);
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Сохранение товара
  const handleSave = async () => {
    try {
      setSaving(true);
      const { images, files, ...payload } = formData;
      payload.product_rating = Number(payload.product_rating) || 0;

      const response = editingProduct
        ? await apiService.adminProducts.update(editingProduct.id, payload)
        : await apiService.adminProducts.create(payload);

      const data = response.data;

      if (data.success) {
        setAlert({
          show: true,
          message: editingProduct ? 'Товар обновлён' : 'Товар создан',
          severity: 'success'
        });
        fetchProducts();
        fetchCategories();
        fetchCrops();
        refreshBarcodeProducts();

        if (editingProduct) {
          handleCloseDialog();
        } else {
          // После создания остаёмся в диалоге в режиме редактирования — можно загружать изображения и файлы
          setEditingProduct(data.data);
          setFormData(prev => ({
            ...prev,
            images: data.data.images || [],
            files: data.data.files || [],
          }));
        }
      } else {
        setAlert({ show: true, message: data.message || 'Ошибка сохранения', severity: 'error' });
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Ошибка подключения к серверу';
      setAlert({ show: true, message, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Запрос подтверждения удаления выбранных товаров (по dataIndex из таблицы)
  const handleRequestDelete = (indices) => {
    setProductsToDelete(indices.map((index) => products[index]).filter(Boolean));
  };

  // Удаление выбранных товаров после подтверждения
  const handleConfirmDelete = async () => {
    try {
      setDeleting(true);
      for (const product of productsToDelete) {
        await apiService.adminProducts.delete(product.id);
      }
      setAlert({ show: true, message: `Удалено товаров: ${productsToDelete.length}`, severity: 'success' });
    } catch (error) {
      const message = error.response?.data?.message || 'Ошибка удаления';
      setAlert({ show: true, message, severity: 'error' });
    } finally {
      setDeleting(false);
      setProductsToDelete([]);
      clearSelectionRef.current?.([]);
      fetchProducts();
      refreshBarcodeProducts();
    }
  };

  // Загрузка изображения (только для уже сохранённого товара)
  const handleImageSelect = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !editingProduct) return;

    try {
      setImageUploading(true);
      const form = new FormData();
      form.append('image', file);
      const response = await apiService.adminProducts.uploadImage(editingProduct.id, form);
      const data = response.data;

      if (data.success) {
        setFormData(prev => ({ ...prev, images: [...prev.images, data.data.path] }));
        fetchProducts();
      } else {
        setAlert({ show: true, message: data.message || 'Ошибка загрузки изображения', severity: 'error' });
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Ошибка загрузки изображения';
      setAlert({ show: true, message, severity: 'error' });
    } finally {
      setImageUploading(false);
    }
  };

  // Удаление изображения
  const handleImageDelete = async (imagePath) => {
    if (!editingProduct) return;

    try {
      const response = await apiService.adminProducts.deleteImage(editingProduct.id, imagePath);
      const data = response.data;

      if (data.success) {
        setFormData(prev => ({ ...prev, images: prev.images.filter((path) => path !== imagePath) }));
        fetchProducts();
      } else {
        setAlert({ show: true, message: data.message || 'Ошибка удаления изображения', severity: 'error' });
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Ошибка удаления изображения';
      setAlert({ show: true, message, severity: 'error' });
    }
  };

  // Загрузка файла (инструкция и т.п., только для уже сохранённого товара)
  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !editingProduct) return;

    try {
      setFileUploading(true);
      const form = new FormData();
      form.append('file', file);
      form.append('name', file.name);
      const response = await apiService.adminProducts.uploadFile(editingProduct.id, form);
      const data = response.data;

      if (data.success) {
        setFormData(prev => ({ ...prev, files: [...prev.files, data.data] }));
        fetchProducts();
      } else {
        setAlert({ show: true, message: data.message || 'Ошибка загрузки файла', severity: 'error' });
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Ошибка загрузки файла';
      setAlert({ show: true, message, severity: 'error' });
    } finally {
      setFileUploading(false);
    }
  };

  // Удаление файла
  const handleFileDelete = async (filePath) => {
    if (!editingProduct) return;

    try {
      const response = await apiService.adminProducts.deleteFile(editingProduct.id, filePath);
      const data = response.data;

      if (data.success) {
        setFormData(prev => ({ ...prev, files: prev.files.filter((file) => file.path !== filePath) }));
        fetchProducts();
      } else {
        setAlert({ show: true, message: data.message || 'Ошибка удаления файла', severity: 'error' });
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Ошибка удаления файла';
      setAlert({ show: true, message, severity: 'error' });
    }
  };

  const columns = [
    {
      name: "image",
      label: "Фото",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) => typeof value === 'string' && value
          ? (
            <Box
              component="img"
              src={getImageUrl(value)}
              alt=""
              sx={{ width: 50, height: 50, objectFit: 'contain', borderRadius: 1 }}
            />
          )
          : <ImageIcon color="disabled" />
      }
    },
    {
      name: "product_code",
      label: "Артикул",
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value) => (
          <Chip
            label={value}
            color="primary"
            size="small"
            variant="outlined"
          />
        )
      }
    },
    {
      name: "product_name",
      label: "Наименование",
      options: {
        filter: false,
        sort: true,
      }
    },
    {
      name: "category",
      label: "Категория",
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => value || '—'
      }
    },
    {
      name: "product_description",
      label: "Описание",
      options: {
        filter: false,
        sort: false,
        setCellProps: () => ({
          style: { maxWidth: "350px" }
        }),
        customBodyRender: (value) => (
          <Typography variant="body2" noWrap sx={{ maxWidth: 350 }}>
            {value || '—'}
          </Typography>
        )
      }
    },
  ];

  const data = products.map((item) => ({
    image: item.images?.[0] || null,
    product_code: item.product_code || undefined,
    product_name: item.product_name || undefined,
    category: item.category || undefined,
    product_description: item.product_description || undefined,
  }));

  const options = {
    storageKey: 'products-table',
    jumpToPage: true,
    rowsPerPageOptions: [10, 25, 50, 100],
    filterType: 'multiselect',
    selectableRows: canEdit ? 'multiple' : 'none',
    customToolbarSelect: (selectedRows, displayData, setSelectedRows) => {
      if (!canEdit) return null;
      clearSelectionRef.current = setSelectedRows;
      const selectedIndices = selectedRows.data.map((row) => row.dataIndex);
      return (
        <Box sx={{ display: 'flex', flexDirection: 'row' }}>
          <Tooltip title={selectedIndices.length === 1 ? 'Редактировать' : 'Редактировать можно только один товар'}>
            <span>
              <IconButton
                disabled={selectedIndices.length !== 1}
                onClick={() => {
                  const product = products[selectedIndices[0]];
                  if (product) handleOpenDialog(product);
                  setSelectedRows([]);
                }}
              >
                <EditIcon />
              </IconButton>
            </span>
          </Tooltip>
          {isAdmin && (
            <Tooltip title="Удалить выбранные">
              <IconButton
                onClick={() => handleRequestDelete(selectedIndices)}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      );
    },
    downloadOptions: {
      filename: `Товары.${new Date().toISOString().split('T')[0]}.csv`,
      filterOptions: {
        useDisplayedColumnsOnly: true,
        useDisplayedRowsOnly: true,
      }
    },
    textLabels: {
      body: {
        noMatch: loading ? (<Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>) : ('Подходящих записей не найдено'),
        toolTip: "Сортировка",
        columnHeaderTooltip: (column) => `Сортировка для ${column.label}`
      },
      pagination: {
        next: "Следующая страница",
        previous: "Предыдущая страница",
        rowsPerPage: "Строк на странице:",
        displayRows: "из",
        jumpToPage: "Перейти на страницу:",
      },
      toolbar: {
        search: "Поиск",
        downloadCsv: "Скачать CSV",
        print: "Печать",
        viewColumns: "Просмотр столбцов",
        filterTable: "Фильтр таблицы",
      },
      filter: {
        all: "Все",
        title: "ФИЛЬТРЫ",
        reset: "СБРОС",
      },
      viewColumns: {
        title: "Показать столбцы",
        titleAria: "Показать/скрыть столбцы таблицы",
      },
      selectedRows: {
        text: "выбрано",
        delete: "Удалить",
        deleteAria: "Удалить выбранные строки",
      },
    },
  };

  const appTheme = useTheme();
  const getMuiTheme = () => createTheme(appTheme, {
    components: {
      MUIDataTableToolbar: {
        styleOverrides: {
          filterPaper: {
            width: '25%',
            minWidth: '20vw',
          },
        },
      },
      MuiGrid: {
        styleOverrides: {
          root: {
            flexBasis: '100% !important',
            maxWidth: '100% !important',
            width: '100%',
            marginLeft: '0',
          },
          item: {
            paddingLeft: '0 !important',
            paddingTop: '1rem !important',
          }
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: '1rem',
            minWidth: '20vw !important',
            padding: '0.5rem !important',
          }
        }
      },
      MUIDataTableViewCol: {
        styleOverrides: {
          root: {
            padding: '1.5rem !important',
          }
        }
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            padding: '0.5rem !important',
          }
        }
      },
    },
  });

  return (
    <>
      {/* Заголовок */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h5" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
            Управление товарами
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Товары (препараты для лечения растений): создание, изображения, описания. Эти товары используются в разделе «Подлинность товаров»
          </Typography>
        </Box>
        {canEdit && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Добавить товар
          </Button>
        )}
      </Box>

      {/* Таблица товаров */}
      <ThemeProvider theme={getMuiTheme()}>
        <MUIDataTable
          title={"Товары"}
          data={data}
          columns={columns}
          options={options}
        />
      </ThemeProvider>

      {/* Модальное окно для создания/редактирования */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingProduct ? 'Редактировать товар' : 'Добавить товар'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Наименование *"
                value={formData.product_name}
                onChange={(e) => handleFormChange('product_name', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Артикул (2 символа) *"
                value={formData.product_code}
                onChange={(e) => handleFormChange('product_code', e.target.value)}
                inputProps={{ maxLength: 2 }}
                helperText="Используется в баркодах"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Autocomplete
                freeSolo
                options={categories}
                value={formData.category}
                onChange={(event, newValue) => handleFormChange('category', newValue || '')}
                onInputChange={(event, newInputValue) => handleFormChange('category', newInputValue)}
                renderInput={(params) => (
                  <TextField {...params} label="Категория" fullWidth />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Autocomplete
                multiple
                freeSolo
                options={cropOptions}
                value={formData.crops}
                onChange={(event, newValue) => handleFormChange('crops', newValue)}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip label={option} {...getTagProps({ index })} />
                  ))
                }
                renderInput={(params) => (
                  <TextField {...params} label="Культуры" fullWidth />
                )}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Рейтинг"
                value={formData.product_rating}
                onChange={(e) => handleFormChange('product_rating', e.target.value)}
                inputProps={{ min: 0, max: 5, step: 1 }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Срок годности"
                value={formData.product_best_before}
                onChange={(e) => handleFormChange('product_best_before', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Упаковка"
                value={formData.product_pack}
                onChange={(e) => handleFormChange('product_pack', e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Описание"
                value={formData.product_description}
                onChange={(e) => handleFormChange('product_description', e.target.value)}
                inputProps={{ maxLength: 2000 }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="О производстве"
                value={formData.product_manufacturer}
                onChange={(e) => handleFormChange('product_manufacturer', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Период защитного действия"
                value={formData.product_safeperiod}
                onChange={(e) => handleFormChange('product_safeperiod', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Норма расхода"
                value={formData.product_consumption}
                onChange={(e) => handleFormChange('product_consumption', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Действующее вещество"
                value={formData.product_composition}
                onChange={(e) => handleFormChange('product_composition', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Вредный объект"
                value={formData.product_implement}
                onChange={(e) => handleFormChange('product_implement', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Где применять"
                value={formData.where_to_apply}
                onChange={(e) => handleFormChange('where_to_apply', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Как применять"
                value={formData.how_to_apply}
                onChange={(e) => handleFormChange('how_to_apply', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Совместимость"
                value={formData.product_compatible}
                onChange={(e) => handleFormChange('product_compatible', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Механизм действия"
                value={formData.product_mech}
                onChange={(e) => handleFormChange('product_mech', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Препаративная форма"
                value={formData.product_form}
                onChange={(e) => handleFormChange('product_form', e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Ссылка на Ozon"
                value={formData.ozon}
                onChange={(e) => handleFormChange('ozon', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Ссылка на видео"
                value={formData.video}
                onChange={(e) => handleFormChange('video', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Ссылка на верификацию"
                value={formData.verify}
                onChange={(e) => handleFormChange('verify', e.target.value)}
              />
            </Grid>

            {/* Изображения товара */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Изображения
              </Typography>
              {editingProduct ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                  {formData.images.map((path) => (
                    <Box key={path} sx={{ position: 'relative' }}>
                      <Box
                        component="img"
                        src={getImageUrl(path)}
                        alt=""
                        sx={{ width: 80, height: 80, objectFit: 'contain', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}
                      />
                      {canEdit && (
                        <IconButton
                          size="small"
                          onClick={() => handleImageDelete(path)}
                          sx={{
                            position: 'absolute',
                            top: -8,
                            right: -8,
                            backgroundColor: 'background.paper',
                            boxShadow: 1,
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  ))}
                  {canEdit && (
                    <>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={handleImageSelect}
                      />
                      <Button
                        variant="outlined"
                        startIcon={imageUploading ? <CircularProgress size={18} /> : <CloudUploadIcon />}
                        onClick={() => fileInputRef.current?.click()}
                        disabled={imageUploading}
                      >
                        Загрузить
                      </Button>
                    </>
                  )}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Сначала сохраните товар — после этого станет доступна загрузка изображений
                </Typography>
              )}
            </Grid>

            {/* Файлы товара (инструкции и т.п.) */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Файлы
              </Typography>
              {editingProduct ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-start' }}>
                  {formData.files.map((file) => (
                    <Box key={file.path} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <FileIcon color="action" fontSize="small" />
                      <Link href={file.url} target="_blank" rel="noopener" underline="hover">
                        {file.name}
                      </Link>
                      {canEdit && (
                        <IconButton size="small" onClick={() => handleFileDelete(file.path)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  ))}
                  {canEdit && (
                    <>
                      <input
                        ref={docInputRef}
                        type="file"
                        accept=".pdf,image/*"
                        style={{ display: 'none' }}
                        onChange={handleFileSelect}
                      />
                      <Button
                        variant="outlined"
                        startIcon={fileUploading ? <CircularProgress size={18} /> : <CloudUploadIcon />}
                        onClick={() => docInputRef.current?.click()}
                        disabled={fileUploading}
                      >
                        Загрузить файл
                      </Button>
                    </>
                  )}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Сначала сохраните товар — после этого станет доступна загрузка файлов
                </Typography>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{editingProduct ? 'Закрыть' : 'Отмена'}</Button>
          {canEdit && (
            <Button
              onClick={handleSave}
              variant="contained"
              disabled={saving || !formData.product_name || formData.product_code.length !== 2}
            >
              {saving ? 'Сохранение…' : (editingProduct ? 'Обновить' : 'Создать')}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Диалог подтверждения удаления */}
      <Dialog
        open={productsToDelete.length > 0}
        onClose={() => !deleting && setProductsToDelete([])}
      >
        <DialogTitle>Подтверждение удаления</DialogTitle>
        <DialogContent>
          <Typography>
            {productsToDelete.length === 1
              ? `Вы уверены, что хотите удалить товар "${productsToDelete[0]?.product_name}"?`
              : `Вы уверены, что хотите удалить товаров: ${productsToDelete.length}?`}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Внимание: уже сгенерированные партии баркодов этих товаров останутся в системе проверки подлинности.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProductsToDelete([])} disabled={deleting}>Отмена</Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            {deleting ? 'Удаление…' : 'Удалить'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Уведомления */}
      <Snackbar
        open={alert.show}
        autoHideDuration={6000}
        onClose={() => setAlert({ ...alert, show: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={alert.severity}
          onClose={() => setAlert({ ...alert, show: false })}
        >
          {alert.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AdminProductsPage;
